import React, { useState, useRef, useEffect, useMemo } from "react";
import Avvvatars from "avvvatars-react";
import LoadingBar from "@components/ui/LoadingBar";
import Icon1 from "@assets/icons/Icon";
import { useMessagerieStore } from "@store/messagerieStore";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import SearchField from "@components/ui/textfields/SearchField";
import SideMenuButton from "@navigation/SideMenuButton";
import BaseModal from "@components/modals/BaseModal";
import TextField from "@components/ui/textfields/TextField";
import Button from "@components/ui/buttons/Button";
import EntityChatPanel from "@components/chat/ChatPanel";
import type {
  DiscussionTab,
  ChatMember,
  Discussion,
  ChatMessage,
} from "../types/messaging";

// --- Constants ---

const EMOJI_LIST = [
  "👍",
  "❤️",
  "😂",
  "🔥",
  "😍",
  "👏",
  "😮",
  "😢",
  "🙏",
  "💯",
  "✅",
  "🎉",
];

// --- Avatar helpers ---

function MemberAvatar({
  member,
  size = 24,
}: {
  member: ChatMember;
  size?: number;
}) {
  return (
    <div
      className="flex-shrink-0 overflow-hidden"
      style={{ borderRadius: 999 }}
    >
      <Avvvatars value={member.name} style="shape" size={size} />
    </div>
  );
}

function AvatarStack({
  members,
  size = 24,
  maxVisible = 3,
  fixedWidth,
}: {
  members: ChatMember[];
  size?: number;
  maxVisible?: number;
  fixedWidth?: number;
}) {
  const visible = members.slice(0, maxVisible);
  const hiddenCount = members.length - maxVisible;
  const overlap = size * 0.58;

  return (
    <div
      className="flex flex-row items-center flex-shrink-0"
      style={fixedWidth ? { width: fixedWidth } : undefined}
    >
      {visible.map((m, i) => (
        <div
          key={m.id}
          className="flex-shrink-0"
          style={{
            marginLeft: i === 0 ? 0 : -overlap,
            zIndex: maxVisible - i + 1,
          }}
        >
          <MemberAvatar member={m} size={size} />
        </div>
      ))}
      {hiddenCount > 0 && (
        <span
          className="text-h2"
          style={{
            marginLeft: 2,
            fontFamily: "Poppins",
            fontWeight: 500,
            color: "#A1A1A1",
          }}
        >
          +{hiddenCount}
        </span>
      )}
    </div>
  );
}

// --- Discussion list item ---

function DiscussionItem({ discussion }: { discussion: Discussion }) {
  const selectedId = useMessagerieStore((s) => s.selectedDiscussionId);
  const setSelected = useMessagerieStore((s) => s.setSelectedDiscussion);
  const members = useMessagerieStore((s) => s.members);
  const isSelected = selectedId === discussion.id;

  const discMembers = members.filter((m) => discussion.members.includes(m.id));

  return (
    <button
      onClick={() => setSelected(discussion.id)}
      className="flex flex-row items-center w-full text-left"
      style={{
        padding: 10,
        gap: 25,
        height: 56,
        borderRadius: 10,
        background: isSelected ? "#F5F5F5" : "transparent",
      }}
    >
      <AvatarStack
        members={discMembers}
        size={28}
        maxVisible={3}
        fixedWidth={55}
      />
      <div className="flex flex-col flex-1" style={{ minWidth: 0 }}>
        <div className="flex flex-row justify-between items-end">
          <span
            className="text-h1"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              lineHeight: "21px",
              color: "#000000",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {discussion.name}
          </span>
          <span
            className="text-h4 text-neutral-400"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              lineHeight: "18px",
              flexShrink: 0,
            }}
          >
            {discussion.lastMessageTime}
          </span>
        </div>
        <div
          className="flex flex-row justify-between items-start"
          style={{ gap: 5 }}
        >
          <span
            className="text-h3 text-neutral-400"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              lineHeight: "15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {discussion.lastMessage}
          </span>
          {discussion.unread && (
            <div
              className="flex items-center justify-center"
              style={{ width: 29, flexShrink: 0 }}
            >
              <div
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 999,
                  background: "#6366f1",
                }}
              />
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// --- Discussions panel (left) ---

const TAB_OPTIONS: { id: DiscussionTab; label: string }[] = [
  { id: "generales", label: "Générales" },
  { id: "ordres", label: "Ordres" },
  { id: "privees", label: "Privées" },
];

function DiscussionSkeleton() {
  return (
    <div className="flex flex-row items-center" style={{ padding: "10px 8px", gap: 12 }}>
      <div className="w-10 h-10 rounded-full bg-neutral-200 animate-pulse flex-shrink-0" />
      <div className="flex flex-col flex-1 gap-1.5">
        <div className="h-3.5 w-32 rounded bg-neutral-200 animate-pulse" />
        <div className="h-3 w-48 rounded bg-neutral-100 animate-pulse" />
      </div>
      <div className="h-3 w-10 rounded bg-neutral-100 animate-pulse flex-shrink-0" />
    </div>
  );
}

function DiscussionsPanel() {
  const activeTab = useMessagerieStore((s) => s.activeTab);
  const setActiveTab = useMessagerieStore((s) => s.setActiveTab);
  const getUnreadCount = useMessagerieStore((s) => s.getUnreadCount);
  const searchQuery = useMessagerieStore((s) => s.searchQuery);
  const setSearchQuery = useMessagerieStore((s) => s.setSearchQuery);
  const isLoadingDiscussions = useMessagerieStore((s) => s.isLoadingDiscussions);
  const getFilteredDiscussions = useMessagerieStore(
    (s) => s.getFilteredDiscussions,
  );

  const [showNewDiscussion, setShowNewDiscussion] = useState(false);

  const discussions = getFilteredDiscussions();

  const tabs = TAB_OPTIONS.map((t) => {
    const count = getUnreadCount(t.id);
    return {
      value: t.id,
      label: count > 0 ? `${t.label} •${count}` : t.label,
    };
  });

  return (
    <div
      className="flex flex-col flex-shrink-0"
      style={{
        width: 380,
        height: "100%",
        padding: "19px 20px 23px",
        gap: 25,
        background: "#FFFFFF",
        borderRight: "1px solid #DDDDDD",
        overflowX: "hidden",
      }}
    >
      <div
        className="flex flex-row items-center justify-between"
        style={{ padding: "0 8px" }}
      >
        <span
          style={{
            fontFamily: "Poppins",
            fontWeight: 500,
            fontSize: 16,
            lineHeight: "24px",
            color: "#000000",
          }}
        >
          Discussions
        </span>
        <button
          onClick={() => setShowNewDiscussion(true)}
          className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
          style={{ width: 32, height: 32 }}
        >
          <Icon1 name="plus" size={14} color="#6366f1" />
        </button>
      </div>
      {showNewDiscussion && (
        <NewDiscussionModal onClose={() => setShowNewDiscussion(false)} />
      )}

      <ChunkyTabs
        activeTab={activeTab}
        tabs={tabs}
        setActiveTab={(tab) => setActiveTab(tab as DiscussionTab)}
      />

      <SearchField
        value={searchQuery}
        onChange={(v: string) => setSearchQuery(v)}
        placeholder="Chercher .."
      />

      <div
        className="flex flex-col flex-1"
        style={{ overflowY: "auto", minHeight: 0 }}
      >
        {isLoadingDiscussions && discussions.length === 0 ? (
          <>
            {Array.from({ length: 8 }).map((_, i) => (
              <DiscussionSkeleton key={i} />
            ))}
          </>
        ) : (
          discussions.map((d) => (
            <DiscussionItem key={d.id} discussion={d} />
          ))
        )}
      </div>
    </div>
  );
}

// --- New discussion modal ---

function NewDiscussionModal({ onClose }: { onClose: () => void }) {
  const members = useMessagerieStore((s) => s.members);
  const currentUserId = useMessagerieStore((s) => s.currentUserId);
  const createDiscussion = useMessagerieStore((s) => s.createDiscussion);

  const [name, setName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [tab, setTab] = useState<DiscussionTab>("generales");
  const [memberSearch, setMemberSearch] = useState("");

  const otherMembers = members.filter((m) => m.id !== currentUserId);
  const filteredMembers = memberSearch
    ? otherMembers.filter((m) =>
        m.name.toLowerCase().includes(memberSearch.toLowerCase()),
      )
    : otherMembers;

  const toggleMember = (id: string) => {
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id],
    );
  };

  const handleCreate = () => {
    if (!name.trim() || selectedMembers.length === 0) return;
    createDiscussion(name.trim(), selectedMembers, tab);
    onClose();
  };

  return (
    <BaseModal
      onClose={onClose}
      title="Nouvelle discussion"
      startIcon={<Icon1 name="side-messagerie" size={20} />}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        <TextField
          label="Nom"
          value={name}
          onChange={(v) => setName(v)}
          placeholder="Nom de la discussion"
        />

        {/* Tab */}
        <div className="flex flex-col gap-1">
          <span className="text-h4 text-neutral-400">Catégorie</span>
          <div className="flex flex-row gap-2">
            {(
              [
                { id: "generales", label: "Générales" },
                { id: "ordres", label: "Ordres" },
                { id: "privees", label: "Privées" },
              ] as { id: DiscussionTab; label: string }[]
            ).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`text-h3 font-medium px-3 py-1.5 rounded-lg transition-colors ${
                  tab === t.id
                    ? "bg-indigo-500 text-white"
                    : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Members */}
        <div className="flex flex-col gap-1">
          <span className="text-h4 text-neutral-400">
            Membres ({selectedMembers.length})
          </span>
          <SearchField
            value={memberSearch}
            onChange={(v) => setMemberSearch(v)}
            placeholder="Rechercher un membre"
          />
          <div
            className="flex flex-col mt-1"
            style={{ maxHeight: 200, overflowY: "auto", gap: 2 }}
          >
            {filteredMembers.map((m) => {
              const selected = selectedMembers.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggleMember(m.id)}
                  className={`flex flex-row items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 ${
                    selected ? "bg-indigo-50" : ""
                  }`}
                >
                  <MemberAvatar member={m} size={28} />
                  <span className="text-h3 font-medium text-black flex-1 text-left">
                    {m.name}
                  </span>
                  {selected && (
                    <Icon1 name="check" size={14} color="#6366f1" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          label="Créer"
          onClick={handleCreate}
          disabled={!name.trim() || selectedMembers.length === 0}
          variant="large"
        />
      </div>
    </BaseModal>
  );
}

// --- Mention text renderer ---

function RenderMessageText({
  text,
  mentions,
}: {
  text: string;
  mentions?: string[];
}) {
  const getMember = useMessagerieStore((s) => s.getMember);
  const members = useMessagerieStore((s) => s.members);

  if (!mentions || mentions.length === 0) {
    return <>{text}</>;
  }

  // Build a regex that matches @MemberName for mentioned members
  const mentionNames = mentions
    .map((id) => getMember(id))
    .filter(Boolean)
    .map((m) => m!.name);

  if (mentionNames.length === 0) return <>{text}</>;

  const pattern = new RegExp(
    `(@(?:${mentionNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}))`,
    "g",
  );

  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, i) => {
        if (
          part.startsWith("@") &&
          mentionNames.some((n) => part === `@${n}`)
        ) {
          return (
            <span key={i} className="font-semibold text-indigo-500">
              {part}
            </span>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

// --- Emoji picker ---

function EmojiPicker({
  onSelect,
  onClose,
  alignRight,
}: {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  alignRight?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute z-50"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        bottom: "100%",
        ...(alignRight ? { right: 0 } : { left: 0 }),
        marginBottom: 4,
        padding: 6,
        background: "#FFFFFF",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: 10,
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: 2,
      }}
    >
      {EMOJI_LIST.map((emoji) => (
        <button
          key={emoji}
          onClick={() => onSelect(emoji)}
          className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
          style={{ width: 32, height: 32, fontSize: 18 }}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

// --- Reaction pill with tooltip ---

function ReactionPill({
  emoji,
  userIds,
  isMine,
  onToggle,
}: {
  emoji: string;
  userIds: string[];
  isMine: boolean;
  onToggle: () => void;
}) {
  const getMember = useMessagerieStore((s) => s.getMember);
  const [showTooltip, setShowTooltip] = useState(false);

  const names = userIds
    .map((id) => getMember(id))
    .filter(Boolean)
    .map((m) => m!.name);

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Tooltip */}
      {showTooltip && (
        <div
          className="absolute z-50"
          style={{
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginBottom: 4,
            padding: "4px 8px",
            background: "#333333",
            borderRadius: 6,
            whiteSpace: "nowrap",
          }}
        >
          <span
            className="text-h5"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              color: "#FFFFFF",
            }}
          >
            {names.join(", ")}
          </span>
        </div>
      )}
      <button
        onClick={onToggle}
        className="flex items-center justify-center"
        style={{
          height: 20,
          padding: 0,
          gap: 1,
          background: "transparent",
          border: "none",
        }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>{emoji}</span>
        {userIds.length > 1 && (
          <span
            className="text-h5"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: 11,
              color: "#999999",
            }}
          >
            {userIds.length}
          </span>
        )}
      </button>
    </div>
  );
}

// --- Bubble radius helpers (WhatsApp style) ---

type BubblePosition = "solo" | "first" | "middle" | "last";

function getBubbleRadius(isOwn: boolean, position: BubblePosition): string {
  if (position === "solo") return isOwn ? "10px 10px 4px 10px" : "10px 10px 10px 4px";
  if (isOwn) {
    if (position === "first") return "10px 10px 4px 10px";
    if (position === "middle") return "10px 4px 4px 10px";
    return "10px 4px 10px 10px"; // last
  }
  // other
  if (position === "first") return "10px 10px 10px 4px";
  if (position === "middle") return "4px 10px 10px 4px";
  return "4px 10px 10px 10px"; // last
}

// --- Message group type ---

interface MessageGroupData {
  senderId: string;
  messages: ChatMessage[];
  isOwn: boolean;
}

function groupMessages(messages: ChatMessage[], currentUserId: string): MessageGroupData[] {
  const groups: MessageGroupData[] = [];
  for (const msg of messages) {
    const sid = msg.sender_id || msg.senderId || "";
    const lastGroup = groups[groups.length - 1];
    // Break group on deleted messages or different sender
    if (msg.deleted || !lastGroup || lastGroup.senderId !== sid || lastGroup.messages.some(m => m.deleted)) {
      groups.push({ senderId: sid, messages: [msg], isOwn: sid === currentUserId });
    } else {
      lastGroup.messages.push(msg);
    }
  }
  return groups;
}

// --- Chat message bubble (WhatsApp style) ---

function MessageBubble({
  message,
  position,
  showTimestamp,
}: {
  message: ChatMessage;
  position: BubblePosition;
  showTimestamp: boolean;
}) {
  const getMember = useMessagerieStore((s) => s.getMember);
  const getMessageById = useMessagerieStore((s) => s.getMessageById);
  const toggleReaction = useMessagerieStore((s) => s.toggleReaction);
  const setReplyingTo = useMessagerieStore((s) => s.setReplyingTo);
  const deleteMessage = useMessagerieStore((s) => s.deleteMessage);
  const currentUserId = useMessagerieStore((s) => s.currentUserId);

  const [hovered, setHovered] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const msgSenderId = message.sender_id || message.senderId || "";
  const sender = getMember(msgSenderId);
  if (!sender) return null;

  const isOwn = msgSenderId === currentUserId;

  // Group reactions by emoji
  const groupedReactions = message.reactions.reduce(
    (acc, r) => {
      const uid = r.user_id || r.userId || "";
      if (!acc[r.emoji]) acc[r.emoji] = [];
      acc[r.emoji].push(uid);
      return acc;
    },
    {} as Record<string, string[]>,
  );
  const hasReactions = Object.keys(groupedReactions).length > 0;

  // Reply source
  const msgReplyToId = message.reply_to_id || message.replyToId;
  const replySource = msgReplyToId
    ? getMessageById(msgReplyToId)
    : undefined;
  const replySender = replySource ? getMember(replySource.sender_id || replySource.senderId || "") : undefined;

  // Download handler
  const handleDownload = async (url?: string, base64?: string, name?: string) => {
    const href = base64 || url;
    if (!href) {
      alert(`Téléchargement simulé : ${name || "fichier"}`);
      return;
    }
    try {
      const res = await fetch(href);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(href, "_blank");
    }
  };

  // Bubble colors
  const bubbleBg = isOwn ? "#000000" : "#F5F5F5";
  const bubbleRadius = getBubbleRadius(isOwn, position);

  // --- Deleted message ---
  if (message.deleted) {
    return (
      <div
        style={{
          padding: "8px 12px",
          background: bubbleBg,
          borderRadius: bubbleRadius,
          maxWidth: "65%",
          opacity: 0.7,
        }}
      >
        <span
          className="text-h2 italic text-neutral-400"
          style={{ fontFamily: "Poppins" }}
        >
          Ce message a été supprimé
        </span>
      </div>
    );
  }

  // --- Normal message ---
  return (
    <div
      className="relative"
      style={{
        minWidth: 80,
        width: "fit-content",
        paddingBottom: hasReactions ? 14 : 0,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        if (!showEmojiPicker) {
          setHovered(false);
        }
      }}
    >
      {/* Hover action bar */}
      {(hovered || showEmojiPicker) && (
        <div
          className="absolute flex flex-row items-center z-10"
          style={{
            top: -8,
            ...(isOwn ? { left: -8 } : { right: -8 }),
            gap: 2,
            padding: "2px 4px",
            background: "#FFFFFF",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.12)",
            borderRadius: 8,
          }}
        >
          <div className="relative">
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
              style={{ width: 28, height: 28 }}
            >
              <span style={{ fontSize: 14 }}>😀</span>
            </button>
            {showEmojiPicker && (
              <EmojiPicker
                alignRight={isOwn}
                onSelect={(emoji) => {
                  toggleReaction(message.id, emoji);
                  setShowEmojiPicker(false);
                  setHovered(false);
                }}
                onClose={() => {
                  setShowEmojiPicker(false);
                  setHovered(false);
                }}
              />
            )}
          </div>
          <button
            onClick={() => setReplyingTo(message.id)}
            className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
            style={{ width: 28, height: 28 }}
          >
            <Icon1 name="response" size={14} color="#A1A1A1" />
          </button>
          {isOwn && (
            <button
              onClick={() => deleteMessage(message.id)}
              className="hover:bg-red-50 rounded-md flex items-center justify-center"
              style={{ width: 28, height: 28 }}
            >
              <Icon1 name="delete" size={14} color="#ef4444" />
            </button>
          )}
        </div>
      )}

      {/* Bubble */}
      <div
        style={{
          background: bubbleBg,
          borderRadius: bubbleRadius,
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
          overflow: "hidden",
        }}
      >
        {/* Reply quote */}
        {replySource && replySender && (
          <div
            style={{
              margin: "6px 8px 0",
              padding: "6px 10px",
              background: isOwn
                ? "rgba(255,255,255,0.5)"
                : "rgba(0,0,0,0.03)",
              borderRadius: 8,
              borderLeft: "3px solid #525252",
              maxWidth: 280,
            }}
          >
            <div
              className="text-h4 font-semibold"
              style={{
                fontFamily: "Poppins",
                color: isOwn ? "#D4D4D4" : "#525252",
                marginBottom: 2,
              }}
            >
              {replySender.name}
            </div>
            <div
              className="text-h4 text-neutral-400"
              style={{
                fontFamily: "Poppins",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {replySource.deleted
                ? "Ce message a été supprimé"
                : replySource.text || "📎 Pièce jointe"}
            </div>
          </div>
        )}

        {/* Image attachment */}
        {message.attachment?.type === "image" && (
          <div
            className="relative group"
            style={{
              margin: message.text ? "6px 4px 0" : 0,
              borderRadius: message.text ? 8 : undefined,
              overflow: "hidden",
              width: 240,
              height: 280,
            }}
          >
            <img
              src={message.attachment.base64Data || message.attachment.url}
              alt={message.attachment.name || "image"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            <button
              onClick={() =>
                handleDownload(
                  message.attachment!.url,
                  message.attachment!.base64Data,
                  message.attachment!.name || "image.png",
                )
              }
              className="absolute opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              style={{
                bottom: 8,
                right: 8,
                width: 32,
                height: 32,
                background: "rgba(255,255,255,0.9)",
                borderRadius: 8,
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.15)",
              }}
            >
              <Icon1 name="download" size={16} color="#3DA7FF" />
            </button>
          </div>
        )}

        {/* File attachment */}
        {message.attachment?.type === "file" && (
          <div
            className="flex flex-row items-center cursor-pointer"
            onClick={() =>
              handleDownload(
                message.attachment!.url,
                message.attachment!.base64Data,
                message.attachment!.name,
              )
            }
            style={{
              padding: "8px 12px",
              gap: 10,
            }}
          >
            <Icon1
              name="file"
              size={16}
              color={isOwn ? "#FFFFFF" : "#000000"}
            />
            <span
              className="text-h2"
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                lineHeight: "15px",
                color: isOwn ? "#FFFFFF" : "#000000",
                flex: 1,
              }}
            >
              {message.attachment.name}
            </span>
            <div
              className="flex items-center justify-center"
              style={{
                width: 24,
                height: 24,
                background: isOwn ? "rgba(255,255,255,0.5)" : "#EBF6FF",
                borderRadius: 5,
              }}
            >
              <Icon1 name="download" size={16} color="#3DA7FF" />
            </div>
          </div>
        )}

        {/* Text content + timestamp */}
        <div style={{ padding: "8px 12px 6px" }}>
          {message.text && (
            <span
              className="text-h2"
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                lineHeight: "20px",
                color: isOwn ? "#FFFFFF" : "#000000",
                whiteSpace: "pre-wrap",
              }}
            >
              <RenderMessageText
                text={message.text}
                mentions={message.mentions}
              />
            </span>
          )}
          {showTimestamp && (
            <div className="flex justify-end" style={{ marginTop: 2 }}>
              <span
                className="text-h5"
                style={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  color: isOwn ? "#A3A3A3" : "#A1A1A1",
                  marginLeft: 12,
                }}
              >
                {message.timestamp}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Reactions */}
      {hasReactions && (
        <div
          className="flex flex-row flex-wrap absolute"
          style={{
            bottom: 0,
            ...(isOwn ? { right: 8 } : { left: 8 }),
            gap: 1,
          }}
        >
          {Object.entries(groupedReactions).map(([emoji, userIds]) => {
            const isMine = userIds.includes(currentUserId);
            return (
              <ReactionPill
                key={emoji}
                emoji={emoji}
                userIds={userIds}
                isMine={isMine}
                onToggle={() => toggleReaction(message.id, emoji)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- Message group component ---

function MessageGroup({ group }: { group: MessageGroupData }) {
  const getMember = useMessagerieStore((s) => s.getMember);
  const sender = getMember(group.senderId);
  if (!sender) return null;

  const count = group.messages.length;

  return (
    <div
      className="flex flex-row"
      style={{
        justifyContent: group.isOwn ? "flex-end" : "flex-start",
        gap: 8,
      }}
    >
      {/* Avatar — only for non-own messages, shown once */}
      {!group.isOwn && (
        <div className="flex-shrink-0" style={{ width: 32, paddingTop: 2 }}>
          <MemberAvatar member={sender} size={32} />
        </div>
      )}

      <div
        className="flex flex-col"
        style={{
          alignItems: group.isOwn ? "flex-end" : "flex-start",
          gap: 3,
          maxWidth: "65%",
        }}
      >
        {/* Sender name — only for non-own messages, shown once */}
        {!group.isOwn && (
          <span
            className="text-h3 font-semibold"
            style={{
              fontFamily: "Poppins",
              color: "#525252",
              marginBottom: 2,
              marginLeft: 4,
            }}
          >
            {sender.name}
          </span>
        )}

        {/* Stacked bubbles */}
        {group.messages.map((msg, i) => {
          const position: BubblePosition =
            count === 1 ? "solo" : i === 0 ? "first" : i === count - 1 ? "last" : "middle";
          const showTimestamp = i === count - 1; // only on last message
          return (
            <MessageBubble
              key={msg.id}
              message={msg}
              position={position}
              showTimestamp={showTimestamp}
            />
          );
        })}
      </div>
    </div>
  );
}

// --- Attachment menu ---

function AttachmentMenu({
  onImageSelect,
  onFileSelect,
  onClose,
}: {
  onImageSelect: () => void;
  onFileSelect: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute z-50 flex flex-col"
      style={{
        bottom: "100%",
        left: 0,
        marginBottom: 8,
        background: "#FFFFFF",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: 10,
        overflow: "hidden",
        minWidth: 160,
      }}
    >
      <button
        onClick={onImageSelect}
        className="flex flex-row items-center hover:bg-neutral-50 text-h2"
        style={{
          padding: "10px 14px",
          gap: 10,
          fontFamily: "Poppins",
          fontWeight: 500,
          color: "#000000",
        }}
      >
        <Icon1 name="image" size={16} color="#A1A1A1" />
        Image
      </button>
      <button
        onClick={onFileSelect}
        className="flex flex-row items-center hover:bg-neutral-50 text-h2"
        style={{
          padding: "10px 14px",
          gap: 10,
          fontFamily: "Poppins",
          fontWeight: 500,
          color: "#000000",
        }}
      >
        <Icon1 name="join" size={16} color="#A1A1A1" />
        Fichier
      </button>
    </div>
  );
}

// --- Chat input ---

function ChatInput() {
  const sendMessage = useMessagerieStore((s) => s.sendMessage);
  const sendTyping = useMessagerieStore((s) => s.sendTyping);
  const replyingToMessageId = useMessagerieStore((s) => s.replyingToMessageId);
  const setReplyingTo = useMessagerieStore((s) => s.setReplyingTo);
  const getMessageById = useMessagerieStore((s) => s.getMessageById);
  const getMember = useMessagerieStore((s) => s.getMember);
  const getMatchingMembers = useMessagerieStore((s) => s.getMatchingMembers);

  const [inputText, setInputText] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStartIdx, setMentionStartIdx] = useState<number>(0);
  const [trackedMentions, setTrackedMentions] = useState<string[]>([]);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const replyMessage = replyingToMessageId
    ? getMessageById(replyingToMessageId)
    : undefined;
  const replySender = replyMessage
    ? getMember(replyMessage.sender_id || replyMessage.senderId || "")
    : undefined;

  const matchingMembers =
    mentionQuery !== null ? getMatchingMembers(mentionQuery) : [];

  useEffect(() => {
    if (replyingToMessageId) {
      inputRef.current?.focus();
    }
  }, [replyingToMessageId]);

  const handleSend = () => {
    if (!inputText.trim()) return;
    sendMessage(
      inputText,
      undefined,
      trackedMentions.length > 0 ? trackedMentions : undefined,
    );
    setInputText("");
    setTrackedMentions([]);
    setMentionQuery(null);
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (mentionQuery !== null && matchingMembers.length > 0) {
        handleMentionSelect(matchingMembers[0]);
      } else {
        handleSend();
      }
    }
    if (e.key === "Escape" && mentionQuery !== null) {
      setMentionQuery(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputText(value);
    sendTyping();

    // Auto-resize
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";

    // Detect @ mentions
    const cursorPos = e.target.selectionStart || value.length;
    const textBefore = value.substring(0, cursorPos);
    const atIdx = textBefore.lastIndexOf("@");

    if (atIdx >= 0) {
      const charBefore = atIdx > 0 ? textBefore[atIdx - 1] : " ";
      if (charBefore === " " || charBefore === "\n" || atIdx === 0) {
        const query = textBefore.substring(atIdx + 1);
        if (!query.includes(" ")) {
          setMentionQuery(query);
          setMentionStartIdx(atIdx);
          return;
        }
      }
    }
    setMentionQuery(null);
  };

  const handleMentionSelect = (member: ChatMember) => {
    const before = inputText.substring(0, mentionStartIdx);
    const after = inputText.substring(
      mentionStartIdx + 1 + (mentionQuery?.length || 0),
    );
    const newText = `${before}@${member.name} ${after}`;
    setInputText(newText);
    setMentionQuery(null);
    setTrackedMentions((prev) =>
      prev.includes(member.id) ? prev : [...prev, member.id],
    );
    inputRef.current?.focus();
  };

  const handleImageSelect = () => {
    setShowAttachMenu(false);
    imageInputRef.current?.click();
  };

  const handleFileSelect = () => {
    setShowAttachMenu(false);
    fileInputRef.current?.click();
  };

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    sendMessage("", undefined, undefined, file);
    e.target.value = "";
  };

  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    sendMessage("", undefined, undefined, file);
    e.target.value = "";
  };

  return (
    <div className="flex flex-col justify-end" style={{ flexShrink: 0 }}>
      {/* Reply preview */}
      {replyMessage && replySender && (
        <div
          className="flex flex-row items-center justify-between"
          style={{
            padding: "8px 12px",
            marginBottom: 4,
            background: "#F5F5F5",
            borderRadius: "10px 10px 0 0",
            borderLeft: "3px solid #525252",
          }}
        >
          <div
            className="flex flex-col"
            style={{ gap: 2, overflow: "hidden", flex: 1 }}
          >
            <span
              className="text-h3 font-semibold"
              style={{ fontFamily: "Poppins", color: "#525252" }}
            >
              {replySender.name}
            </span>
            <span
              className="text-h4 text-neutral-400"
              style={{
                fontFamily: "Poppins",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {replyMessage.text || "📎 Pièce jointe"}
            </span>
          </div>
          <button
            onClick={() => setReplyingTo(null)}
            className="flex-shrink-0 hover:bg-neutral-200 rounded-full flex items-center justify-center"
            style={{ width: 24, height: 24 }}
          >
            <Icon1 name="close" size={10} color="#A1A1A1" />
          </button>
        </div>
      )}

      <div
        className="flex flex-row items-center relative"
        style={{
          padding: "8px 10px",
          gap: 10,
          background: "#F5F5F5",
          borderRadius: replyMessage ? "0 0 10px 10px" : 10,
        }}
      >
        {/* Attachment button */}
        <div className="relative">
          <button
            onClick={() => setShowAttachMenu(!showAttachMenu)}
            className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
            style={{ width: 28, height: 28 }}
          >
            <Icon1 name="plus" size={12} color="#A1A1A1" />
          </button>
          {showAttachMenu && (
            <AttachmentMenu
              onImageSelect={handleImageSelect}
              onFileSelect={handleFileSelect}
              onClose={() => setShowAttachMenu(false)}
            />
          )}
        </div>

        {/* Text input */}
        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Entrez un message"
            rows={1}
            className="w-full outline-none border-none text-h2 resize-none"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              color: "#000000",
              background: "transparent",
              maxHeight: 120,
              overflowY: "auto",
            }}
          />

          {/* Mention dropdown */}
          {mentionQuery !== null && matchingMembers.length > 0 && (
            <div
              className="absolute z-50 flex flex-col"
              style={{
                bottom: "100%",
                left: 0,
                marginBottom: 12,
                background: "#FFFFFF",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                borderRadius: 10,
                overflow: "hidden",
                minWidth: 200,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {matchingMembers.map((member) => (
                <button
                  key={member.id}
                  onClick={() => handleMentionSelect(member)}
                  className="flex flex-row items-center hover:bg-neutral-50"
                  style={{
                    padding: "8px 12px",
                    gap: 10,
                  }}
                >
                  <MemberAvatar member={member} size={24} />
                  <div className="flex flex-col items-start">
                    <span
                      className="text-h2"
                      style={{
                        fontFamily: "Poppins",
                        fontWeight: 500,
                        color: "#000000",
                      }}
                    >
                      {member.name}
                    </span>
                    <span
                      className="text-h4 text-neutral-400"
                      style={{ fontFamily: "Poppins" }}
                    >
                      {member.role}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Send button */}
        <button
          onClick={handleSend}
          className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
          style={{ width: 28, height: 28 }}
        >
          <Icon1
            name="send"
            size={16}
            color={inputText.trim() ? "#525252" : "#A1A1A1"}
          />
        </button>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageFile}
        style={{ display: "none" }}
      />
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileAttach}
        style={{ display: "none" }}
      />
    </div>
  );
}

// --- Chat panel (right) ---

// --- Chat header actions dropdown ---

function ChatActionsDropdown({
  onInfo,
  onAddMembers,
  onMuteNotifs,
  onLeave,
  isMuted,
}: {
  onInfo: () => void;
  onAddMembers: () => void;
  onMuteNotifs: () => void;
  onLeave: () => void;
  isMuted: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="hover:bg-neutral-200 rounded-md flex items-center justify-center"
        style={{ width: 32, height: 32 }}
      >
        <Icon1 name="down" size={16} color="#666666" />
      </button>
      {open && (
        <div
          className={`
            ${visible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-90"}
            absolute top-full right-0 z-50 w-300 transition-all duration-200 flex flex-col text-shadow_50 border-1 border-shadow_10 bg-white shadow-xl rounded-lg
          `}
          style={{ marginTop: 4 }}
        >
          <div className="flex flex-col p-1">
            <SideMenuButton
              size="large"
              label="Infos de la conversation"
              name="question"
              onClick={() => { onInfo(); setOpen(false); }}
            />
            <SideMenuButton
              size="large"
              label="Ajouter des membres"
              name="user"
              onClick={() => { onAddMembers(); setOpen(false); }}
            />
            <SideMenuButton
              size="large"
              label={isMuted ? "Réactiver les notifications" : "Couper les notifications"}
              name="cloche"
              onClick={() => { onMuteNotifs(); setOpen(false); }}
            />
          </div>
          <div className="flex flex-col border-t-1 p-1">
            <SideMenuButton
              size="large"
              label="Quitter la conversation"
              name="logout"
              color="#ef4444"
              onClick={() => { onLeave(); setOpen(false); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// --- Add members modal ---

function AddMembersModal({
  onClose,
  discussionId,
}: {
  onClose: () => void;
  discussionId: string;
}) {
  const members = useMessagerieStore((s) => s.members);
  const discussions = useMessagerieStore((s) => s.discussions);
  const currentUserId = useMessagerieStore((s) => s.currentUserId);

  const discussion = discussions.find((d) => d.id === discussionId);
  const existingMembers = discussion?.members || [];

  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  const available = members.filter(
    (m) => m.id !== currentUserId && !existingMembers.includes(m.id),
  );
  const filtered = search
    ? available.filter((m) =>
        m.name.toLowerCase().includes(search.toLowerCase()),
      )
    : available;

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleAdd = () => {
    if (selected.length === 0 || !discussion) return;
    onClose();
  };

  return (
    <BaseModal
      onClose={onClose}
      title="Ajouter des membres"
      startIcon={<Icon1 name="user" size={20} />}
      width="max-w-sm"
    >
      <div className="flex flex-col gap-3 p-4">
        <SearchField
          value={search}
          onChange={(v) => setSearch(v)}
          placeholder="Rechercher"
        />
        <div
          className="flex flex-col"
          style={{ maxHeight: 240, overflowY: "auto", gap: 2 }}
        >
          {filtered.length === 0 && (
            <span className="text-h3 text-neutral-400 py-3 text-center">
              Aucun membre disponible
            </span>
          )}
          {filtered.map((m) => {
            const sel = selected.includes(m.id);
            return (
              <button
                key={m.id}
                onClick={() => toggle(m.id)}
                className={`flex flex-row items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 ${
                  sel ? "bg-indigo-50" : ""
                }`}
              >
                <MemberAvatar member={m} size={28} />
                <span className="text-h3 font-medium text-black flex-1 text-left">
                  {m.name}
                </span>
                {sel && <Icon1 name="check" size={14} color="#6366f1" />}
              </button>
            );
          })}
        </div>
        <Button
          label={`Ajouter (${selected.length})`}
          onClick={handleAdd}
          disabled={selected.length === 0}
          variant="large"
        />
      </div>
    </BaseModal>
  );
}

// --- Conversation info modal ---

// --- Conv info side panel ---

type InfoTab = "medias" | "fichiers" | "liens";

function ConvInfoPanel({
  onClose,
  discussion,
  chatMembers,
  messages,
}: {
  onClose: () => void;
  discussion: Discussion;
  chatMembers: ChatMember[];
  messages: ChatMessage[];
}) {
  const [activeTab, setActiveTab] = useState<InfoTab>("medias");

  const handleDownload = async (url?: string, base64?: string, name?: string) => {
    const href = base64 || url;
    if (!href) {
      alert(`Téléchargement simulé : ${name || "fichier"}`);
      return;
    }
    try {
      const res = await fetch(href);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(href, "_blank");
    }
  };

  const medias = messages.filter(
    (m) => m.attachment?.type === "image",
  );
  const files = messages.filter(
    (m) => m.attachment?.type === "file",
  );

  // Extract URLs from message texts
  const urlRegex = /https?:\/\/[^\s]+/g;
  const links: { url: string; messageId: string }[] = [];
  messages.forEach((m) => {
    if (m.text) {
      const matches = m.text.match(urlRegex);
      if (matches) {
        matches.forEach((url) => links.push({ url, messageId: m.id }));
      }
    }
  });

  const tabs: { id: InfoTab; label: string; count: number }[] = [
    { id: "medias", label: "Médias", count: medias.length },
    { id: "fichiers", label: "Fichiers", count: files.length },
    { id: "liens", label: "Liens", count: links.length },
  ];

  return (
    <div
      className="flex flex-col flex-shrink-0"
      style={{
        width: 340,
        height: "100%",
        background: "#FFFFFF",
        borderLeft: "1px solid #EEEEEE",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <div
        className="flex flex-row items-center justify-between flex-shrink-0"
        style={{ padding: "20px 20px 0" }}
      >
        <span
          style={{
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: 16,
            color: "#000000",
          }}
        >
          Infos
        </span>
        <button
          onClick={onClose}
          className="hover:bg-neutral-100 rounded-full flex items-center justify-center"
          style={{ width: 28, height: 28 }}
        >
          <Icon1 name="close" size={12} color="#A1A1A1" />
        </button>
      </div>

      {/* Conv name & description */}
      <div className="flex flex-col flex-shrink-0" style={{ padding: "16px 20px", gap: 12 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span
            style={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: 18,
              color: "#000000",
            }}
          >
            {discussion.name}
          </span>
          {discussion.description && (
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: 13,
                color: "#888888",
              }}
            >
              {discussion.description}
            </span>
          )}
        </div>
      </div>

      {/* Members */}
      <div
        className="flex flex-col flex-shrink-0"
        style={{
          padding: "0 20px 16px",
          gap: 8,
          borderBottom: "1px solid #F0F0F0",
        }}
      >
        <span
          style={{
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: 13,
            color: "#A1A1A1",
          }}
        >
          Membres ({chatMembers.length})
        </span>
        <div className="flex flex-col" style={{ gap: 2 }}>
          {chatMembers.map((m) => (
            <div
              key={m.id}
              className="flex flex-row items-center"
              style={{ gap: 10, padding: "5px 0" }}
            >
              <MemberAvatar member={m} size={30} />
              <div className="flex flex-col flex-1" style={{ gap: 0 }}>
                <span
                  style={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: 13,
                    color: "#000000",
                  }}
                >
                  {m.name}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: 11,
                    color: "#AAAAAA",
                  }}
                >
                  {m.role}
                </span>
              </div>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: m.online ? "#5EEAD4" : "#D4D4D4",
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Tabs: Medias / Fichiers / Liens */}
      <div
        className="flex flex-row flex-shrink-0"
        style={{ padding: "12px 20px 0", gap: 0 }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className="flex-1 flex items-center justify-center"
            style={{
              fontFamily: "Poppins",
              fontWeight: activeTab === t.id ? 600 : 500,
              fontSize: 13,
              color: activeTab === t.id ? "#000000" : "#AAAAAA",
              padding: "8px 0",
              background: "transparent",
              borderTop: "none",
              borderLeft: "none",
              borderRight: "none",
              borderBottomWidth: 2,
              borderBottomStyle: "solid",
              borderBottomColor: activeTab === t.id ? "#6366f1" : "transparent",
            }}
          >
            {t.label} {t.count > 0 && <span style={{ marginLeft: 4, color: "#AAAAAA", fontWeight: 400 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1" style={{ padding: "12px 20px", overflowY: "auto", minHeight: 0 }}>
        {activeTab === "medias" && (
          medias.length === 0 ? (
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 13,
                color: "#CCCCCC",
                display: "block",
                textAlign: "center",
                marginTop: 30,
              }}
            >
              Aucun média
            </span>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 4,
              }}
            >
              {medias.map((m) => (
                <div
                  key={m.id}
                  className="group relative cursor-pointer"
                  style={{
                    aspectRatio: "1",
                    borderRadius: 6,
                    overflow: "hidden",
                    background: "#F5F5F5",
                  }}
                  onClick={() =>
                    handleDownload(
                      m.attachment!.url,
                      m.attachment!.base64Data,
                      m.attachment!.name || "image",
                    )
                  }
                >
                  <img
                    src={m.attachment!.base64Data || m.attachment!.url}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: "rgba(0,0,0,0.35)" }}
                  >
                    <Icon1 name="download" size={20} color="#FFFFFF" />
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === "fichiers" && (
          files.length === 0 ? (
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 13,
                color: "#CCCCCC",
                display: "block",
                textAlign: "center",
                marginTop: 30,
              }}
            >
              Aucun fichier
            </span>
          ) : (
            <div className="flex flex-col" style={{ gap: 4 }}>
              {files.map((m) => (
                <div
                  key={m.id}
                  className="flex flex-row items-center hover:bg-neutral-50 cursor-pointer"
                  style={{
                    padding: "8px 10px",
                    gap: 10,
                    borderRadius: 8,
                  }}
                  onClick={() =>
                    handleDownload(
                      m.attachment!.url,
                      m.attachment!.base64Data,
                      m.attachment!.name,
                    )
                  }
                >
                  <Icon1 name="file" size={18} color="#6366f1" />
                  <span
                    style={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: 13,
                      color: "#000000",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.attachment!.name || "Fichier"}
                  </span>
                  <Icon1 name="download" size={14} color="#BBBBBB" />
                  <span
                    style={{
                      fontFamily: "Poppins",
                      fontWeight: 400,
                      fontSize: 11,
                      color: "#BBBBBB",
                      flexShrink: 0,
                    }}
                  >
                    {m.timestamp}
                  </span>
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === "liens" && (
          links.length === 0 ? (
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 13,
                color: "#CCCCCC",
                display: "block",
                textAlign: "center",
                marginTop: 30,
              }}
            >
              Aucun lien
            </span>
          ) : (
            <div className="flex flex-col" style={{ gap: 4 }}>
              {links.map((l, i) => (
                <a
                  key={i}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-row items-center hover:bg-neutral-50"
                  style={{
                    padding: "8px 10px",
                    gap: 10,
                    borderRadius: 8,
                    textDecoration: "none",
                  }}
                >
                  <Icon1 name="linkedFile" size={18} color="#6366f1" />
                  <span
                    style={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: 13,
                      color: "#6366f1",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {l.url}
                  </span>
                </a>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function FlatChatPanel({ onToggleInfo }: { onToggleInfo: () => void }) {
  const selectedDiscussionId = useMessagerieStore(
    (s) => s.selectedDiscussionId,
  );
  const allMessages = useMessagerieStore((s) => s.messages);
  const discussions = useMessagerieStore((s) => s.discussions);
  const members = useMessagerieStore((s) => s.members);
  const isLoadingMessages = useMessagerieStore((s) => s.isLoadingMessages);
  const isLoadingOlderMessages = useMessagerieStore((s) => s.isLoadingOlderMessages);
  const hasMoreMessages = useMessagerieStore((s) => s.hasMoreMessages);
  const fetchOlderMessages = useMessagerieStore((s) => s.fetchOlderMessages);
  const typingUsers = useMessagerieStore((s) => s.typingUsers);
  const getMember = useMessagerieStore((s) => s.getMember);
  const currentUserId = useMessagerieStore((s) => s.currentUserId);

  const discussion = discussions.find((d) => d.id === selectedDiscussionId);
  const messages = selectedDiscussionId
    ? allMessages[selectedDiscussionId] || []
    : [];
  const chatMembers = discussion
    ? members.filter((m) => discussion.members.includes(m.id))
    : [];
  const onlineCount = chatMembers.filter((m) => m.online).length;

  const currentTyping = selectedDiscussionId
    ? (typingUsers[selectedDiscussionId] || [])
        .map((id) => getMember(id)?.name)
        .filter(Boolean)
    : [];

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [convSearch, setConvSearch] = useState("");
  const [showConvSearch, setShowConvSearch] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const prevMessageCountRef = useRef(0);

  // Auto-scroll to bottom on new messages (not when loading older ones)
  useEffect(() => {
    const isNewMessage = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;
    if (isNewMessage || !prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" as ScrollBehavior });
    }
  }, [messages.length, selectedDiscussionId]);

  // Reset counter when switching discussions
  useEffect(() => {
    prevMessageCountRef.current = 0;
  }, [selectedDiscussionId]);

  // Scroll-up detection for loading older messages
  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el || !selectedDiscussionId) return;
    if (el.scrollTop < 100 && hasMoreMessages[selectedDiscussionId] && !isLoadingOlderMessages) {
      const prevScrollHeight = el.scrollHeight;
      fetchOlderMessages(selectedDiscussionId).then(() => {
        // Preserve scroll position after prepending older messages
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight - prevScrollHeight;
          }
        });
      });
    }
  };

  useEffect(() => {
    setShowConvSearch(false);
    setConvSearch("");
  }, [selectedDiscussionId]);

  const filteredMessages = convSearch
    ? messages.filter(
        (m) =>
          m.text && m.text.toLowerCase().includes(convSearch.toLowerCase()),
      )
    : messages;

  const messageGroups = groupMessages(filteredMessages, currentUserId);

  if (!discussion) {
    return (
      <div
        className="flex-1 flex items-center justify-center"
        style={{ background: "#FAFAFA" }}
      >
        <span
          className="text-h1"
          style={{
            fontFamily: "Poppins",
            fontWeight: 500,
            color: "#A1A1A1",
          }}
        >
          Sélectionnez une discussion
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col flex-1"
      style={{
        padding: "30px 20px",
        gap: 15,
        background: "#FAFAFA",
        minWidth: 0,
        height: "100%",
      }}
    >
      {/* Header */}
      <div className="flex flex-col" style={{ gap: 4 }}>
        <div className="flex flex-row items-center justify-between">
          <span
            style={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: 20,
              lineHeight: "30px",
              color: "#000000",
            }}
          >
            {discussion.name}
          </span>
          <div className="flex flex-row items-center" style={{ gap: 4 }}>
            <button
              onClick={() => {
                setShowConvSearch(!showConvSearch);
                if (showConvSearch) setConvSearch("");
              }}
              className="hover:bg-neutral-200 rounded-md flex items-center justify-center"
              style={{
                width: 32,
                height: 32,
                background: showConvSearch ? "#F0F0F0" : "transparent",
              }}
            >
              <Icon1 name="search" size={16} color="#666666" />
            </button>
            <ChatActionsDropdown
              onInfo={onToggleInfo}
              onAddMembers={() => setShowAddMembers(true)}
              onMuteNotifs={() => setIsMuted(!isMuted)}
              onLeave={() => {}}
              isMuted={isMuted}
            />
          </div>
        </div>
        {showConvSearch && (
          <input
            autoFocus
            value={convSearch}
            onChange={(e) => setConvSearch(e.target.value)}
            placeholder="Rechercher dans la conversation..."
            className="outline-none border-none text-h3"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              color: "#000000",
              background: "#F0F0F0",
              borderRadius: 8,
              padding: "8px 12px",
              marginTop: 4,
            }}
          />
        )}
        <div
          className="flex flex-row items-center"
          style={{ gap: 20, marginTop: 4 }}
        >
          <AvatarStack members={chatMembers} size={24} />
          <div className="flex flex-row items-center" style={{ gap: 5 }}>
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "#5EEAD4",
              }}
            />
            <span
              className="text-h2"
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                lineHeight: "18px",
                color: "#5EEAD4",
              }}
            >
              {onlineCount} en ligne
            </span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex flex-col flex-1"
        style={{ gap: 16, overflowY: "auto", minHeight: 0, padding: "0 10px" }}
      >
        {isLoadingOlderMessages && (
          <div className="flex justify-center" style={{ padding: 12 }}>
            <span
              className="text-h3"
              style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
            >
              Chargement...
            </span>
          </div>
        )}
        {messageGroups.map((group) => (
          <MessageGroup key={group.messages[0].id} group={group} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      {currentTyping.length > 0 && (
        <div
          style={{
            padding: "4px 16px",
            fontSize: 12,
            color: "#A1A1A1",
            fontFamily: "Poppins",
            fontStyle: "italic",
          }}
        >
          {currentTyping.join(", ")}{" "}
          {currentTyping.length === 1 ? "écrit" : "écrivent"}...
        </div>
      )}

      {/* Modals */}
      {showAddMembers && selectedDiscussionId && (
        <AddMembersModal
          onClose={() => setShowAddMembers(false)}
          discussionId={selectedDiscussionId}
        />
      )}
      {/* Input */}
      <ChatInput />
    </div>
  );
}

// --- Chat panel router: entity-linked → ChatPanel, otherwise flat ---

function ChatPanelSkeleton() {
  return (
    <div className="flex-1 flex flex-col" style={{ background: "#FAFAFA" }}>
      {/* Header skeleton */}
      <div className="flex flex-row items-center px-5 py-3 border-b border-neutral-200 bg-white" style={{ gap: 12 }}>
        <div className="w-10 h-10 rounded-full bg-neutral-200 animate-pulse flex-shrink-0" />
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="h-4 w-36 rounded bg-neutral-200 animate-pulse" />
          <div className="h-3 w-20 rounded bg-neutral-100 animate-pulse" />
        </div>
      </div>

      {/* Messages skeleton */}
      <div className="flex-1 flex flex-col px-5 py-4" style={{ gap: 16 }}>
        {/* Left-aligned messages */}
        <div className="flex flex-row items-end gap-2" style={{ maxWidth: "60%" }}>
          <div className="w-7 h-7 rounded-full bg-neutral-200 animate-pulse flex-shrink-0" />
          <div className="flex flex-col gap-1.5">
            <div className="h-10 w-52 rounded-xl bg-neutral-200 animate-pulse" />
            <div className="h-8 w-36 rounded-xl bg-neutral-200 animate-pulse" />
          </div>
        </div>

        {/* Right-aligned message */}
        <div className="flex flex-row justify-end">
          <div className="h-10 w-44 rounded-xl bg-neutral-200 animate-pulse" />
        </div>

        {/* Left-aligned message */}
        <div className="flex flex-row items-end gap-2" style={{ maxWidth: "60%" }}>
          <div className="w-7 h-7 rounded-full bg-neutral-200 animate-pulse flex-shrink-0" />
          <div className="h-14 w-64 rounded-xl bg-neutral-200 animate-pulse" />
        </div>

        {/* Right-aligned messages */}
        <div className="flex flex-col items-end gap-1.5">
          <div className="h-8 w-40 rounded-xl bg-neutral-200 animate-pulse" />
          <div className="h-10 w-56 rounded-xl bg-neutral-200 animate-pulse" />
        </div>

        {/* Left-aligned message */}
        <div className="flex flex-row items-end gap-2" style={{ maxWidth: "60%" }}>
          <div className="w-7 h-7 rounded-full bg-neutral-200 animate-pulse flex-shrink-0" />
          <div className="h-8 w-48 rounded-xl bg-neutral-200 animate-pulse" />
        </div>
      </div>

      {/* Input skeleton */}
      <div className="px-5 py-3 border-t border-neutral-200 bg-white">
        <div className="h-10 w-full rounded-xl bg-neutral-100 animate-pulse" />
      </div>
    </div>
  );
}

function ChatPanelRouter({ onToggleInfo }: { onToggleInfo: () => void }) {
  const selectedDiscussionId = useMessagerieStore((s) => s.selectedDiscussionId);
  const discussions = useMessagerieStore((s) => s.discussions);
  const members = useMessagerieStore((s) => s.members);
  const isLoadingDiscussions = useMessagerieStore((s) => s.isLoadingDiscussions);

  const discussion = discussions.find((d) => d.id === selectedDiscussionId);

  // Check if this is an entity-linked (dossier/ordre) discussion
  const isEntityLinked = discussion && (discussion.dossier_id || discussion.ordre_id);

  // Find sibling internal discussion
  const internalDiscussion = useMemo(() => {
    if (!discussion || !isEntityLinked) return undefined;
    return discussions.find((d) =>
      d.type === "internal" &&
      d.id !== discussion.id &&
      (discussion.dossier_id ? d.dossier_id === discussion.dossier_id : d.ordre_id === discussion.ordre_id)
    );
  }, [discussion, discussions, isEntityLinked]);

  const chatMembers = useMemo(() => {
    if (!discussion) return [];
    return members
      .filter((m) => discussion.members.includes(m.id))
      .map((m) => ({ name: m.name }));
  }, [discussion, members]);

  const onlineCount = useMemo(() => {
    if (!discussion) return 0;
    return members.filter((m) => discussion.members.includes(m.id) && m.online).length;
  }, [discussion, members]);

  if (!discussion) {
    // Show skeleton while loading, placeholder when loaded with nothing selected
    if (isLoadingDiscussions && discussions.length === 0) {
      return <ChatPanelSkeleton />;
    }
    return (
      <div className="flex-1 flex items-center justify-center" style={{ background: "#FAFAFA" }}>
        <span className="text-h1" style={{ fontFamily: "Poppins", fontWeight: 500, color: "#A1A1A1" }}>
          Sélectionnez une discussion
        </span>
      </div>
    );
  }

  if (isEntityLinked) {
    return (
      <div className="flex-1 flex flex-col" style={{ minWidth: 0 }}>
        <EntityChatPanel
          discussionId={discussion.id}
          internalDiscussionId={internalDiscussion?.id}
          entityType={discussion.ordre_id ? "ordre" : "dossier"}
          entityId={discussion.ordre_id || discussion.dossier_id}
          entityName={discussion.name}
          members={chatMembers}
          onlineCount={onlineCount}
          clientChannel={discussion.channel}
          whatsappLinked={!!discussion.whatsapp_phone}
        />
      </div>
    );
  }

  return <FlatChatPanel onToggleInfo={onToggleInfo} />;
}

// --- Main Screen ---

export default function MessagerieScreen() {
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const selectedDiscussionId = useMessagerieStore(
    (s) => s.selectedDiscussionId,
  );
  const allMessages = useMessagerieStore((s) => s.messages);
  const discussions = useMessagerieStore((s) => s.discussions);
  const members = useMessagerieStore((s) => s.members);
  const init = useMessagerieStore((s) => s.init);
  const isLoadingDiscussions = useMessagerieStore((s) => s.isLoadingDiscussions);

  const discussion = discussions.find((d) => d.id === selectedDiscussionId);
  const isEntityLinked = discussion && (discussion.dossier_id || discussion.ordre_id);
  const messages = selectedDiscussionId
    ? allMessages[selectedDiscussionId] || []
    : [];
  const chatMembers = discussion
    ? members.filter((m) => discussion.members.includes(m.id))
    : [];

  // Init store on mount (WS stays connected app-wide)
  useEffect(() => {
    init();
    return () => {
      // Clear selection so WS handler knows we're not viewing any discussion
      useMessagerieStore.setState({ selectedDiscussionId: null });
    };
  }, []);

  // Close info panel when switching discussions
  useEffect(() => {
    setShowInfoPanel(false);
  }, [selectedDiscussionId]);

  return (
    <div className="flex flex-row h-screen overflow-hidden relative">
      {isLoadingDiscussions && discussions.length === 0 && <LoadingBar variant="fixed" />}
      <DiscussionsPanel />
      <ChatPanelRouter onToggleInfo={() => setShowInfoPanel((v) => !v)} />
      {showInfoPanel && discussion && !isEntityLinked && (
        <ConvInfoPanel
          onClose={() => setShowInfoPanel(false)}
          discussion={discussion}
          chatMembers={chatMembers}
          messages={messages}
        />
      )}
    </div>
  );
}
